/**
 * The captured effects, ready to play: `content/sfx/c64.json` (the C64's six, run from the game's own
 * driver) and `content/sfx/extras.json` (the remake's extras, run through the same code), both made by
 * `tools/capture-sfx.mjs`. Pure: shared by the browser player, the render tool and the tests.
 */

import c64 from '../../../content/sfx/c64.json';
import extras from '../../../content/sfx/extras.json';
import { base64Bytes } from '../music/musicCues';
import { unpackFrames } from '../sid/capture';
import { C64_EFFECTS, EXTRA_EFFECTS, isEffectName, laneOf, type EffectName } from './effectNames';
import type { SfxScript } from './sfxPlayer';

/** One effect entry of the capture files. */
interface CapturedEffect {
  name: string;
  frames: number;
  data: string;
}

/** Extras held while a jingle has voice 1, rather than dropped (see `SfxPlayer`). */
const WAITS: ReadonlySet<EffectName> = new Set<EffectName>(['hurryUp']);

function decode(list: readonly CapturedEffect[], into: Map<EffectName, SfxScript>): void {
  for (const e of list) {
    if (!isEffectName(e.name)) throw new Error(`sfx capture: unknown effect '${e.name}'`);
    const frames = unpackFrames(base64Bytes(e.data));
    if (frames.length !== e.frames) throw new Error(`sfx capture: ${e.name}: ${frames.length} frames, not ${e.frames}`);
    const script: SfxScript = WAITS.has(e.name)
      ? { name: e.name, lane: laneOf(e.name), frames, waits: true }
      : { name: e.name, lane: laneOf(e.name), frames };
    into.set(e.name, script);
  }
}

/** Every effect by name; throws if a capture file lacks one. */
export function loadEffects(): Map<EffectName, SfxScript> {
  const out = new Map<EffectName, SfxScript>();
  decode(c64.effects, out);
  decode(extras.effects, out);
  for (const name of [...C64_EFFECTS, ...EXTRA_EFFECTS]) {
    if (!out.has(name)) throw new Error(`sfx capture: no '${name}' (rerun tools/capture-sfx.mjs)`);
  }
  return out;
}
